import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  type AvatarProps,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useUserDisplay } from '../../hooks/useUserDisplay';

interface UserAvatarProps {
  userId: string | null | undefined;
  className?: string;
  /** Avatar step from the design system's scale: `xs` 20 - `mini` 24 - `sm` 32 - `default` 40. */
  size?: AvatarProps['size'];
}

/**
 * A user as an avatar alone, for places that identify an owner without room to
 * name one. `PreResolvedAvatarCell` covers avatar-plus-name; this resolves the id
 * itself, which is why it lives here rather than in `shared`.
 *
 * Hovering names the user in a tooltip, which is the only label the circle carries.
 *
 * With an id, the circle renders before the name arrives and while the lookup fails,
 * so a row keeps its shape instead of reflowing once the directory answers. With no id
 * it renders nothing: an empty circle reads as broken, and it hid a stale server
 * dropping the field once already.
 */
export const UserAvatar = ({ userId, className, size = 'sm' }: UserAvatarProps) => {
  const { t } = useTranslation('common');
  const { name, avatarUrl, initials } = useUserDisplay(userId);

  if (!userId) {
    return null;
  }

  const avatar = (
    <Avatar
      data-testid="user-avatar"
      size={size}
      roundness="full"
      className={clsx('shrink-0', className)}
    >
      {avatarUrl ? (
        <AvatarImage src={avatarUrl} alt={name ?? t('components.userAvatar.alt')} />
      ) : null}
      <AvatarFallback size={size} roundness="full">
        {initials ?? ''}
      </AvatarFallback>
    </Avatar>
  );

  if (!name) {
    return avatar;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{avatar}</TooltipTrigger>
        <TooltipContent side="bottom">{name}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

UserAvatar.displayName = 'UserAvatar';
